import {
  ContentProps,
  ExternalPlayerBridge,
  StylesConfig as PlayerStylesConfig,
} from '@player/content';
import { Engine } from '@rendley/sdk';

import type { Content } from '@/entities/content/api/types';
import { config } from '@/shared/lib';

const FALLBACK_HLS = '/video/placeholder.m3u8';

export function getHLSPath(content: Content): string {
  if (!content.media?.hls_path) {
    return FALLBACK_HLS;
  }

  return `${config.MEDIA_CONTENT_URL}/${content.media?.hls_path ?? ''}`;
}

export function buildPlayerProps(
  content: Partial<Content>,
  type: 'video' | 'slider',
  renderedVideoURL?: string,
  resetHLS?: boolean,
  bridge?: ExternalPlayerBridge
): ContentProps & { rendleyEngine?: Engine } {
  if (!content.config) {
    throw new Error('Content config is required');
  }

  // Deep clone and convert app's StylesConfig (with file_path) to player's StylesConfig (with src)
  const { file_path, ...logoRest } = content.config.logo;
  const playerConfig: PlayerStylesConfig = {
    ...structuredClone({ ...content.config, logo: logoRest }),
    logo: {
      ...structuredClone(logoRest),
      src: '', // Will be set below
    },
  };

  // Handle logo src from upload or existing file_path
  if (file_path) {
    playerConfig.logo.src = `${config.MEDIA_CONTENT_URL}${file_path}`;
  }

  if (type === 'video') {
    return {
      type: 'video',
      config: playerConfig,
      text_blocks: (content.text_blocks ?? []).map(({ url, ...rest }) => ({
        ...rest,
        url: url ?? '',
      })),
      path_hls: resetHLS ? '' : getHLSPath(content as Content),
      path_mp4: renderedVideoURL!,
      externalPlayer: bridge,
      callback: {
        onLoaded: () => {
          console.debug('player/content onLoaded');
        },
        // eslint-disable-next-line
        onError: (error: any) => {
          console.error('player/content onError', error);
        },
      },
    };
  }
  return {
    type: 'slider',
    config: playerConfig,
    slides: content.slides || [],
    callback: {
      onLoaded: () => {
        console.debug('player/content onLoaded');
      },
      // eslint-disable-next-line
      onError: (error: any) => {
        console.error('player/content onError', error);
      },
    },
  };
}
